/**
 * Hook that tracks browser online/offline state and pending outbox count.
 * Used by the OfflineBanner and any component that needs to grey out
 * network-dependent actions when offline.
 */

import { useState, useEffect } from 'react'
import { offlineDB } from './db'
import { authService } from '../modules/identity/services/auth'

export interface NetworkStatus {
  isOnline: boolean
  /** Number of writes queued in the outbox waiting to be flushed. */
  pendingWrites: number
  /** True while the sync engine is actively flushing the outbox. */
  isSyncing: boolean
}

export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  )
  const [pendingWrites, setPendingWrites] = useState(0)
  const [isSyncing, setIsSyncing] = useState(false)

  // Track online / offline
  useEffect(() => {
    const onOnline  = () => { setIsOnline(true);  setIsSyncing(true); setTimeout(() => setIsSyncing(false), 3000) }
    const onOffline = () => setIsOnline(false)
    window.addEventListener('online',  onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online',  onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  // Live outbox count — re-queries whenever the table changes
  useEffect(() => {
    const user = authService.getCurrentUser()
    if (!user) return

    const refresh = async () => {
      const count = await offlineDB.outbox.where('userId').equals(user.id).count()
      setPendingWrites(count)
    }

    refresh()
    offlineDB.outbox.hook('creating', refresh)
    offlineDB.outbox.hook('deleting', refresh)
    return () => {
      offlineDB.outbox.hook('creating').unsubscribe(refresh)
      offlineDB.outbox.hook('deleting').unsubscribe(refresh)
    }
  }, [])

  return { isOnline, pendingWrites, isSyncing }
}
